import { APILoggerService } from '@app/logger/api/api-logger.service';
import { AUSnackBarService } from '@app/notifications/au-snackbar.service';
import { AuthorizationService } from '@app/security/shared/authorization.service';
import { AuthGuard } from '@app/security/shared/auth-guard.service';
import { FormErrorLogRequest } from '@app/logger/api/log-request.model';
import { FormService } from '@app/forms/shared/forms.service';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { LocalStorageRefService } from '@app/shared/services/browser/local-storage-ref.service';
import { Logger } from '@app/logger/logger';
import { Observable, throwError } from 'rxjs';
import { SeatTrackerService } from '@app/security/shared/seat-tracker.service';
import { SecurityService } from '@app/security/shared/security.service';
import { catchError, switchMap } from 'rxjs/operators';

const RECOVERABLE_SEAT_ERROR_CODES = [
  'seat_token_header_invalid',
  'seat_token_not_found',
  'seat_id_mismatch'
];

const AUTH_ERROR_MESSAGES: Record<string, string> = {
  token_missing: 'Your session token is missing. Please sign in again.',
  token_invalid: 'Your session token is invalid. Please sign in again.',
  token_expired: 'Your session has expired. Please sign in again.',
  token_not_authenticated: 'Your session could not be authenticated. Please sign in again.',
  username_claim_missing: 'Your session is missing its username. Please sign in again.',
  database_claim_missing: 'Your session is missing its database. Please sign in again.',
  profile_id_claim_missing: 'Your session is missing its user profile. Please sign in again.',
  authentication_instant_claim_missing:
    'Your session is missing authentication information. Please sign in again.',
  seat_id_claim_missing: 'Your session is missing its license seat. Please sign in again.',
  secret_claim_missing: 'Your session is missing required credentials. Please sign in again.',
  admin_only_claim_missing: 'Your session is missing its access mode. Please sign in again.',
  seat_token_header_invalid: 'Your license seat token is missing or invalid. Please sign in again.',
  seat_token_not_found: 'Your license seat could not be recovered. Please sign in again.',
  seat_id_mismatch: 'Your license seat no longer matches this session. Please sign in again.',
  admin_only_access_denied: 'This administration-only session cannot access that resource.',
  group_claim_invalid: 'Your session contains invalid group information. Please sign in again.',
  group_claim_missing: 'Your session is missing group information. Please sign in again.',
  group_assignments_missing: 'Your account is not assigned to a required group.',
  required_group_missing: 'Your account does not have the group required for this resource.',
  authorization_failed: 'Your session could not be authorized. Please sign in again.'
};

@Injectable()
export class UnauthorizedInterceptor implements HttpInterceptor {
  constructor(
    private apiLogger: APILoggerService,
    private auSnackBar: AUSnackBarService,
    private authorizationService: AuthorizationService,
    private formService: FormService,
    private injector: Injector,
    private localStorageService: LocalStorageRefService,
    private logger: Logger,
    private securityService: SecurityService,
    private authGuard: AuthGuard
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    request = request.clone({
      headers: request.headers.set('seat_token', this.localStorageService.seatToken)
    });

    return next.handle(request).pipe(
      catchError((error: any) => {
        if (!(error instanceof HttpErrorResponse)) {
          return throwError(() => error);
        }

        if (this.isSeatAuthenticationError(error)) {
          return this.recoverSeatAndRetry(request, next, error);
        }

        this.handleHttpError(request, error);
        return throwError(() => error);
      })
    );
  }

  private recoverSeatAndRetry(
    request: HttpRequest<any>,
    next: HttpHandler,
    originalError: HttpErrorResponse
  ): Observable<HttpEvent<any>> {
    const seatTrackerService = this.injector.get(SeatTrackerService);

    return seatTrackerService.recoverSeat().pipe(
      switchMap((recovered) => {
        if (!recovered) {
          this.handleHttpError(request, originalError);
          return throwError(() => originalError);
        }

        const retryRequest = request.clone({
          headers: request.headers.set('seat_token', this.localStorageService.seatToken)
        });

        return next.handle(retryRequest).pipe(
          catchError((retryError: any) => {
            if (retryError instanceof HttpErrorResponse) {
              this.handleHttpError(retryRequest, retryError);
            }

            return throwError(() => retryError);
          })
        );
      })
    );
  }

  private isSeatAuthenticationError(error: HttpErrorResponse): boolean {
    // only seat failures are recoverable; token expiration and permission failures log out immediately.
    return error.status === 401 && RECOVERABLE_SEAT_ERROR_CODES.includes(error.error?.code);
  }

  private handleHttpError(request: HttpRequest<any>, error: HttpErrorResponse): void {
    if (error.status === 401) {
      this.authGuard.clearRequestedUrl();
      this.auSnackBar.openError(this.getUnauthorizedMessage(error), undefined, 10000);
      this.logger.log(
        'Received a 401 from the server, logging user out',
        request,
        error,
        '\t(unauthorized.interceptor.ts)'
      );
      this.securityService.forceLogout();
    } else if (error.status === 403) {
      this.authorizationService.displayInsufficientPermissionDialog(error.url);
      this.sendFormErrorLog();
      this.logger.log(
        'Received a 403 from the server',
        request,
        error,
        '\t(unauthorized.interceptor.ts)'
      );
    }
  }

  private getUnauthorizedMessage(error: HttpErrorResponse): string {
    const errorCode = error.error?.code;
    return (
      AUTH_ERROR_MESSAGES[errorCode] ||
      'Your session is no longer authorized. Please sign in again.'
    );
  }

  private sendFormErrorLog(): void {
    const request = new FormErrorLogRequest();
    request.clientUrl = window.location.href;
    request.formId = this.formService.getCurrentForm()?.id?.toString() || 'undefined';
    request.formOverrideId = this.formService.formOverrideId?.toString() || 'undefined';
    request.formIdQueue = this.formService.formOverrideIdQ?.toString() || 'undefined';

    this.apiLogger.sendFormErrorLog(request).subscribe({
      next: (resp) => {
        this.logger.log(resp);
      },
      error: (error) => {
        this.logger.error(error);
      }
    });
  }
}
